/**
 * RESET · Ultra — User Baseline Computation
 *
 * Derives a runner's *current training reality* from their Strava activity
 * history. This object replaces the naive `currentWeeklyKm` single-number
 * input to the plan generator: a plan built on a real, multi-signal baseline
 * is the difference between a coach-quality plan and an injury-bait plan.
 *
 * Canonical reference: `/docs/training_methodology.md` §"User baseline".
 *
 * Why each signal exists:
 *   - medianWeeklyKm     — MEDIAN (not mean) of the last 4 completed weeks.
 *                          Median smooths a single big week or a single
 *                          missed week; mean does not. This anchors the
 *                          volume ramp and the readiness gate.
 *   - consistencyScore   — (weeks with >5 km) / 4. A runner who logged
 *                          3 of 4 weeks is in different shape than one who
 *                          logged 1 of 4 with the same median. Drives the
 *                          "your training has been inconsistent" warning.
 *   - longestRunKm/Sec   — longest single run in the last 60 days, in BOTH
 *                          km and duration. Time-on-feet matters more than
 *                          distance for ultra readiness (§Pillar 4).
 *   - observedThresholdPaceSec — fastest average pace over 3–15 km runs in
 *                          the last 60 days. A real, observed threshold
 *                          proxy. OVERRIDES the Riegel projection in the
 *                          generator when present (see planGenerator.js).
 *   - medianRunPaceSec   — median pace across the runner's recent runs.
 *                          The reference for the pace SANITY GUARD: a plan
 *                          must never prescribe paces wildly faster than
 *                          what the runner actually runs (the Garmin bug).
 *
 * This module is PURE: same activities + same `now` → same output. No I/O,
 * no Date.now() inside the math (now is injected) so it is unit-testable.
 *
 * MIRROR: a byte-identical copy lives at `src/lib/userBaseline.js`. Edit the
 * server copy, then run `node server/test/check-mirror.js` (or copy by hand)
 * — the two files must stay in lockstep. See CLAUDE.md §"Generator mirror".
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/* ─── activity-type sets ──────────────────────────────────────
 * Mirrors the type filters used in server/index.js (stravaFetchRuns /
 * stravaFetchAllActivities) and metricsEngine.js (matchNewPlanSessions). */
const RUN_TYPES = new Set(['Run', 'TrailRun', 'Treadmill', 'VirtualRun']);
const HIKE_TYPES = new Set(['Hike', 'Walk']);

/* ─── small helpers ───────────────────────────────────────────── */

function startOfDay(ts) {
  const r = new Date(ts);
  r.setHours(0, 0, 0, 0);
  return r;
}

/** Monday-00:00 of the week containing `ts`. Matches planGenerator.startOfWeek. */
function startOfWeek(ts) {
  const r = startOfDay(ts);
  const day = r.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday-start
  r.setDate(r.getDate() + diff);
  return r;
}

function median(arr) {
  if (!arr || !arr.length) return null;
  const a = [...arr].sort((x, y) => x - y);
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}

/** Distance in km from a stored activity (distance_m is metres). */
function activityKm(a) {
  const m = Number(a && a.distance_m);
  return Number.isFinite(m) && m > 0 ? m / 1000 : 0;
}

/** Moving time in seconds from a stored activity. */
function activitySec(a) {
  const s = Number(a && a.moving_time_s);
  return Number.isFinite(s) && s > 0 ? s : 0;
}

/** Epoch ms of an activity's start, or NaN. Accepts start_date or date. */
function activityTime(a) {
  const t = new Date((a && (a.start_date || a.date)) || NaN).getTime();
  return Number.isFinite(t) ? t : NaN;
}

/* ─── main ────────────────────────────────────────────────────── */

/**
 * Compute a runner's training baseline.
 *
 * @param {Array}  activities  Strava activities. Each: { id, type, start_date,
 *                             distance_m, moving_time_s, elev_m, ... }.
 * @param {Object} opts
 * @param {boolean} opts.includeHikes  Count Hike/Walk toward weekly volume
 *                                     (mirrors the includeHikesInVolume UI
 *                                     toggle). Pace + longest-run signals
 *                                     stay run-only regardless.
 * @param {number}  opts.now           Epoch ms "now" — injected for testing.
 *
 * @returns {Object} userBaseline:
 *   {
 *     medianWeeklyKm, recentWeeklyKm:[4], weeksActive, consistencyScore,
 *     longestRunKm, longestRunDurationSec, longestRunDate,
 *     observedThresholdPaceSec, observedThresholdSource,
 *     medianRunPaceSec, activityCount, runCount,
 *     dataConfidence, isInconsistent, sampleWindowDays, computedAt
 *   }
 */
function computeUserBaseline(activities, opts = {}) {
  const includeHikes = !!opts.includeHikes;
  const now = Number.isFinite(opts.now) ? opts.now : Date.now();

  const list = Array.isArray(activities) ? activities : [];

  // Volume-eligible types: runs always, hikes/walks only if the user opted in.
  const volumeTypes = new Set(RUN_TYPES);
  if (includeHikes) HIKE_TYPES.forEach((t) => volumeTypes.add(t));

  /* ── 1. Weekly volume buckets — last 4 COMPLETE Mon–Sun weeks ──
   * The current (partial) week is excluded: a half-finished week would
   * understate the baseline. Bucket i covers [thisMonday-(4-i)w, ...+1w). */
  const thisMonday = +startOfWeek(now);
  const weekStartFor = (i) => thisMonday - (4 - i) * WEEK_MS; // i = 0..3
  const recentWeeklyKm = [0, 0, 0, 0];

  for (const a of list) {
    if (!a || !volumeTypes.has(a.type)) continue;
    const t = activityTime(a);
    if (!Number.isFinite(t)) continue;
    const km = activityKm(a);
    if (km <= 0) continue;
    for (let i = 0; i < 4; i++) {
      const ws = weekStartFor(i);
      if (t >= ws && t < ws + WEEK_MS) { recentWeeklyKm[i] += km; break; }
    }
  }
  for (let i = 0; i < 4; i++) recentWeeklyKm[i] = Math.round(recentWeeklyKm[i] * 10) / 10;

  const medianWeeklyKm = Math.round((median(recentWeeklyKm) || 0) * 10) / 10;
  const weeksActive = recentWeeklyKm.filter((v) => v > 5).length;
  const consistencyScore = Math.round((weeksActive / 4) * 100) / 100;

  /* ── 2. Longest run + pace signals — last 60 days, RUNS ONLY ──
   * Hikes/walks are excluded here even when includeHikes is on: a 6 h walk
   * is not a "long run" and walk pace is not a threshold proxy. */
  const sixtyDaysAgo = now - 60 * DAY_MS;
  let longestRunKm = 0;
  let longestRunDurationSec = 0;
  let longestRunDate = null;
  let observedThresholdPaceSec = null; // fastest 3–15 km avg pace
  const runPaces = [];                 // for medianRunPaceSec (3–25 km)
  let runCount = 0;
  let activityCount = 0;

  for (const a of list) {
    if (!a) continue;
    const t = activityTime(a);
    if (!Number.isFinite(t) || t < sixtyDaysAgo || t > now) continue;
    if (volumeTypes.has(a.type)) activityCount++;
    if (!RUN_TYPES.has(a.type)) continue;
    runCount++;

    const km = activityKm(a);
    const sec = activitySec(a);
    if (km <= 0 || sec <= 0) continue;

    // Longest run.
    if (km > longestRunKm) {
      longestRunKm = km;
      longestRunDurationSec = sec;
      longestRunDate = new Date(t).toISOString().slice(0, 10);
    }

    const paceSec = sec / km; // sec per km

    // Observed threshold proxy: fastest 3–15 km run. 3 km min excludes
    // all-out sprints faster than true threshold; 15 km max excludes long
    // runs slower than threshold.
    if (km >= 3 && km <= 15) {
      if (observedThresholdPaceSec == null || paceSec < observedThresholdPaceSec) {
        observedThresholdPaceSec = paceSec;
      }
    }

    // Median run pace reference for the sanity guard: a broad 3–25 km band
    // so it reflects typical training pace, not just easy or just fast runs.
    if (km >= 3 && km <= 25) runPaces.push(paceSec);
  }

  longestRunKm = Math.round(longestRunKm * 10) / 10;
  if (observedThresholdPaceSec != null) {
    observedThresholdPaceSec = Math.round(observedThresholdPaceSec);
  }
  const medianRunPaceSec = runPaces.length ? Math.round(median(runPaces)) : null;

  /* ── 3. Data confidence ──
   * 'none' — nothing to build on; generator falls back to cold-start defaults.
   * 'low'  — thin data; UI warns the baseline may not reflect true capacity.
   * 'good' — enough signal to trust the ramp + readiness math. */
  let dataConfidence;
  if (activityCount === 0) dataConfidence = 'none';
  else if (activityCount < 6 || weeksActive < 3) dataConfidence = 'low';
  else dataConfidence = 'good';

  // Inconsistency flag drives the explicit UI warning per the brief: fewer
  // than 3 of 4 recent weeks active → the baseline may understate capacity.
  const isInconsistent = weeksActive < 3;

  return {
    medianWeeklyKm,
    recentWeeklyKm,
    weeksActive,
    consistencyScore,
    longestRunKm,
    longestRunDurationSec,
    longestRunDate,
    observedThresholdPaceSec,
    observedThresholdSource: observedThresholdPaceSec != null ? 'strava' : null,
    medianRunPaceSec,
    activityCount,
    runCount,
    dataConfidence,
    isInconsistent,
    includeHikes,
    sampleWindowDays: 60,
    computedAt: new Date(now).toISOString(),
  };
}

/**
 * Human-readable one-line summary of a baseline, for the pre-generation card
 * and for logs. Kept here so server and client phrase it identically.
 */
function describeBaseline(b) {
  if (!b || b.dataConfidence === 'none') {
    return 'No recent Strava activity — connect Strava or sync to compute a baseline.';
  }
  const longest = b.longestRunKm > 0
    ? `${b.longestRunKm} km (${Math.round(b.longestRunDurationSec / 60)} min)`
    : 'none ≥ logged';
  return (
    `4-week median ${b.medianWeeklyKm} km/wk · ` +
    `longest run in 60 days ${longest} · ` +
    `active ${b.weeksActive}/4 weeks`
  );
}

module.exports = {
  computeUserBaseline,
  describeBaseline,
  RUN_TYPES,
  HIKE_TYPES,
};
